import * as z from 'zod'
import { injectable } from '~/container'
import { keysToCamel } from '~/utility/toCamel'
import { langFallback$Fetch } from '../../lang/utils/langfetch'
import { csrfTokenService } from '../csrf/CsrfTokenService'
import { UserSignupRepository } from './repositories'
import { SignUpSuccessResponse, SignUpUserData } from './types'

@injectable()
export class UserSignupRepositoryFetch implements UserSignupRepository {
  async signup(
    data: SignUpUserData,
    langCode: string,
  ): Promise<SignUpSuccessResponse> {
    try {
      const response: any = await this.getResponse(data, langCode)

      // Check for JSON errors (new backend format)
      // Legacy backend returns HTML on success, new backend returns JSON
      const errors: string = response?.user_signup?.errors
      if (errors) {
        throw {
          validationErrors: this.mapResponseErrors(
            responseErrorsSchema.parse(JSON.parse(errors)),
          ),
        }
      }

      // If we get here, it's a success
      // Check if the response is JSON with explicit success field
      if (response && typeof response === 'object' && 'success' in response) {
        return {
          success: response.success,
          message:
            response.message ||
            'Account created successfully. Please check your email to verify your account.',
          authenticated: response.authenticated,
          redirect_url: response.redirect_url,
        }
      }

      // Legacy backend returns HTML on success, treat as success
      return {
        success: true,
        message:
          'Account created successfully. Please check your email to verify your account.',
      }
    } catch (error: any) {
      // If it's a validation error, re-throw it
      if (error.validationErrors) {
        throw error
      }

      // Handle server errors (500, network errors, etc.)
      console.error('Signup error:', error)

      // Use server's message if available
      if (error.response?.data?.message) {
        throw {
          message: error.response.data.message,
          originalError: error,
        }
      }

      // Only use generic fallback if no server message
      throw {
        message: 'Signup failed',
        originalError: error,
      }
    }
  }

  private async getResponse(data: SignUpUserData, langCode: string) {
    const { csrfToken } = await csrfTokenService()
    return langFallback$Fetch(langCode, '/accounts/signup/?ajax=1', {
      method: 'POST',
      body: this.getFormData(data),
      headers: {
        'X-ELDARION-AJAX': 'true',
        'X-CSRFToken': csrfToken,
        'X-Requested-With': 'XMLHttpRequest',
      },
    })
  }

  private getFormData(input: SignUpUserData) {
    const formData = new FormData()
    formData.append('email', input.email)
    formData.append('first_name', input.firstName)
    formData.append('last_name', input.lastName)
    formData.append('password1', input.password)
    formData.append('password2', input.password)
    return formData
  }

  private mapResponseErrors(errors: ResponseErrors) {
    const withCamelKeys = keysToCamel(errors)
    // Map password1 to password for the form
    withCamelKeys.password = [...(errors.password1 || [])]
    // Ensure email errors are properly mapped (keysToCamel should handle this, but being explicit)
    withCamelKeys.email = [...(errors.email || [])]
    return withCamelKeys
  }
}

const responseErrorSchema = z.string().array().optional()
const responseErrorsSchema = z.object({
  email: responseErrorSchema,
  first_name: responseErrorSchema,
  last_name: responseErrorSchema,
  password1: responseErrorSchema,
  password2: responseErrorSchema,
})
type ResponseErrors = z.infer<typeof responseErrorsSchema>
